import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BarChart3, ChevronRight, FileSearch, Plus, Search, Table2 } from "lucide-react";
import type { Visualization, Widget } from "@cd/core";
import { api } from "../../lib/api";
import { shownRange } from "../../lib/dateZoom";
import {
  addStep, backTo, defaultGrain, drillBy, drillDefinition, drillQuery, drillRange, drillTitle, drillViz, stepFrom, stepLabel, withBy, type Drill,
} from "../../lib/drill";
import { chipsFor, toDashboardFilters, type PageFilter } from "../../lib/pageFilters";
import type { TimeSelection } from "../../lib/time";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Dialog, Sheet } from "../../components/ui/overlay";
import { Input } from "../../components/ui/primitives";
import { Provenance, TrustBadge, WarningList } from "../../components/viz/Trust";
import type { OnPick } from "../../components/viz/onPick";
import { useWidgetQuery, VizBody, WidgetState } from "../../components/viz/WidgetView";
import { DimensionRow, PickerSection } from "../builder/Catalog";
import { breakdownFields } from "./breakdownFields";
import { PageFilterBadges } from "./PageFilterBadges";

interface Props {
  /** The breakdown (the URL's `d`); the panel is closed without one. */
  drill?: Drill;
  /** The widget it started from; the panel stays closed if it's no longer on the dashboard. */
  widget?: Widget;
  /** `replace` swaps the URL entry rather than adding one, for choices Back shouldn't step through. */
  onChange: (d: Drill | undefined, opts?: { replace?: boolean }) => void;
  time: TimeSelection;
  locationIds?: string[];
  pageFilters: PageFilter[];
  /** Whether "Save as widget" is offered (people who can edit the dashboard). */
  canEdit: boolean;
  onSaved: (w: Widget) => void;
}

/**
 * "Break down … by…": a temporary widget beside the dashboard, from the clicked widget's metrics
 * narrowed to the clicked values and split by a field the viewer picks. Its own clicks drill further
 * rather than filtering the dashboard, and each step is a breadcrumb to go back to.
 */
export function BreakdownPanel({ drill, widget, onChange, time, locationIds, pageFilters, canEdit, onSaved }: Props) {
  const open = !!drill && !!widget;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onChange(undefined)} title={widget ? `Break down ${widget.definition.title}` : ""} width="w-[680px]">
      {open && <PanelBody key={drill.widgetId} {...{ drill, widget, onChange, time, locationIds, pageFilters, canEdit, onSaved }} />}
    </Sheet>
  );
}

function PanelBody({ drill, widget, onChange, time, locationIds, pageFilters, canEdit, onSaved }: Props & { drill: Drill; widget: Widget }) {
  const source = widget.definition.query;
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog, staleTime: 5 * 60_000 });
  const by = drillBy(drill);
  const query = useMemo(() => drillQuery(source, drill, time), [source, drill, time]);
  const { sent, highlight } = useMemo(() => chipsFor(query ?? source, pageFilters), [query, source, pageFilters]);
  const dashboardFilters = useMemo(() => (sent.length ? toDashboardFilters(sent) : undefined), [sent]);
  const q = useWidgetQuery(query ?? null, locationIds, { source: "breakdown", widgetId: widget.id }, dashboardFilters);

  const [asTable, setAsTable] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [work, setWork] = useState(false);
  const [saving, setSaving] = useState(false);
  const viz: Visualization | undefined = by && (asTable ? { type: "table", config: {} } : drillViz(by));

  const packs = catalog.data?.packs;
  const pack = packs && (packs.find((p) => p.name === (source.pack ?? "")) ?? packs[0]);
  const byLabel = by && (pack?.dimensions.find((d) => d.ref === by.ref)?.label ?? by.ref);
  // "Net sales", or the widget's title until the first result names its metrics.
  const metricNames = q.data?.columns.filter((c) => c.kind === "metric").map((c) => c.label).join(", ") || widget.definition.title;

  // A click in the panel narrows it further, then waits for the next field to split by.
  const onPick: OnPick = (picks) => {
    const step = stepFrom(picks, shownRange(q.data));
    if (step) onChange(addStep(drill, step));
  };

  return (
    <div className="flex h-full flex-col">
      <nav aria-label="Breakdown steps" className="flex flex-wrap items-center gap-1 border-b border-border px-5 py-2.5 text-sm">
        <span className="text-muted">{metricNames}</span>
        {drill.steps.map((s, i) => {
          const last = i === drill.steps.length - 1;
          return (
            <Fragment key={i}>
              <ChevronRight className="size-3.5 text-subtle" />
              <button
                onClick={() => !last && onChange(backTo(drill, i))}
                disabled={last}
                className={cn("max-w-48 truncate rounded px-1", last ? "font-medium" : "text-muted hover:bg-surface-2 hover:text-foreground")}
              >
                {stepLabel(s)}
              </button>
            </Fragment>
          );
        })}
        <ChevronRight className="size-3.5 text-subtle" />
        <button onClick={() => setChoosing(!choosing)} className="rounded px-1 text-accent hover:bg-accent-soft" aria-expanded={choosing || !by}>
          {byLabel ? `by ${byLabel}` : "by…"}
        </button>
      </nav>

      {!packs ? (
        <div className="flex-1" />
      ) : !by || choosing ? (
        <FieldChoice
          sections={(qs) => breakdownFields(packs, source, drill, qs)}
          onPick={(ref) => {
            const d = pack?.dimensions.find((x) => x.ref === ref);
            const grain = d?.type === "time" ? defaultGrain(d.grains, drillRange(drill) ?? time.timeRange) : undefined;
            setChoosing(false);
            onChange(withBy(drill, grain ? { ref, grain } : { ref }), { replace: true });
          }}
          title={by ? "Split by another field" : `Split ${stepLabel(drill.steps.at(-1)!)} by`}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-5">
          <div className="flex flex-wrap items-center gap-1.5">
            <PageFilterBadges result={q.data} sent={sent} pageFilters={pageFilters} />
            {q.data && <TrustBadge certified={q.data.certified} compact />}
            <div className="ml-auto flex items-center gap-1">
              <Button size="icon-sm" variant={asTable ? "ghost" : "secondary"} onClick={() => setAsTable(false)} aria-label="Show as chart" aria-pressed={!asTable}>
                <BarChart3 className="size-3.5" />
              </Button>
              <Button size="icon-sm" variant={asTable ? "secondary" : "ghost"} onClick={() => setAsTable(true)} aria-label="Show as table" aria-pressed={asTable}>
                <Table2 className="size-3.5" />
              </Button>
            </div>
          </div>
          <div className={cn("relative h-80 shrink-0", q.isFetching && q.data && "opacity-70 transition-opacity")}>
            <WidgetState query={q} />
            {q.data && !q.error && viz && <VizBody viz={viz} result={q.data} onPick={onPick} highlight={highlight} />}
          </div>
          {q.data && !q.error && <WarningList warnings={q.data.warnings} />}
          <p className="text-xs text-subtle">Click a value to break it down further.</p>
          {work && q.data && (
            <div className="rounded-xl border border-border">
              <Provenance result={q.data} filterLabels={Object.fromEntries(pageFilters.map((c) => [c.ref, c.label]))} />
            </div>
          )}
        </div>
      )}

      {by && !choosing && (
        <footer className="flex items-center gap-2 border-t border-border px-5 py-3">
          <Button size="sm" variant="ghost" onClick={() => setWork(!work)} disabled={!q.data}>
            <FileSearch className="size-3.5" /> {work ? "Hide your work" : "Show your work"}
          </Button>
          {canEdit && (
            <Button size="sm" variant="primary" className="ml-auto" disabled={!q.data || !!q.error} onClick={() => setSaving(true)}>
              <Plus className="size-3.5" /> Save as widget
            </Button>
          )}
        </footer>
      )}
      {/* Mounted on open, so its title starts from the latest result's names. */}
      {canEdit && saving && by && viz && (
        <SaveDialog
          open
          onOpenChange={setSaving}
          defaultTitle={drillTitle(metricNames, drill, byLabel ?? by.ref)}
          datesDropped={!!drillRange(drill)}
          save={(title) => api.createWidget(drillDefinition(source, drill, title, viz)!)}
          onSaved={(w) => {
            setSaving(false);
            onSaved(w);
          }}
        />
      )}
    </div>
  );
}

function FieldChoice({ sections, onPick, title }: {
  sections: (q: string) => ReturnType<typeof breakdownFields>;
  onPick: (ref: string) => void;
  title: string;
}) {
  const [q, setQ] = useState("");
  const section = sections(q);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 p-3">
        <p className="px-1 text-sm font-medium">{title}</p>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-subtle" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields" className="pl-8" autoFocus />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        <PickerSection
          title="Group by"
          section={section}
          several={section.available.length > 1}
          searching={!!q}
          row={(r) => <DimensionRow key={`${r.pack.name}:${r.field.ref}`} row={r} onClick={() => onPick(r.field.ref)} />}
        />
      </div>
    </div>
  );
}

function SaveDialog({ open, onOpenChange, defaultTitle, datesDropped, save, onSaved }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultTitle: string;
  /** A clicked date can't be saved: a dashboard's range replaces a widget's. */
  datesDropped: boolean;
  save: (title: string) => Promise<Widget>;
  onSaved: (w: Widget) => void;
}) {
  const [title, setTitle] = useState(defaultTitle);
  const m = useMutation({ mutationFn: () => save(title), onSuccess: onSaved });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Save as widget" className="w-[min(440px,calc(100vw-32px))] p-5">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        className="space-y-3"
      >
        <h2 className="text-sm font-semibold">Save as widget</h2>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} aria-label="Widget title" autoFocus />
        <p className="text-xs text-muted">
          Adds this breakdown to the dashboard, with the values you clicked saved as its own filters. The page's filters aren't saved with it.
          {datesDropped && " Dates follow the dashboard's range, so the date you clicked isn't saved."}
        </p>
        {m.error && <p className="text-sm text-bad">{(m.error as Error).message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={m.isPending}>{m.isPending ? "Saving…" : "Add to dashboard"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
